import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus } from "react-feather";
import CustomPayment from "../../Componant/Dashboard/Student/profile/payment/custom-payment";
import { getPackageList, studentProfile } from "../../Api/apiUtils";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  TextInput,
  Toolbar,
  Badge,
  BadgeTone,
  Loader,
  EmptyState,
  Callout,
  Dialog,
} from "../../ui";
import "./split-payment.scss";
import {
  SplitGroup,
  SplitPart,
  describeLevels,
  formatCreatedOn,
  formatExpiry,
  isExpired,
  isSettled,
  formatMoney,
  getClassCount,
  getDiscount,
  getCurrencyCode,
  getDiscountPercent,
  getGross,
  getPartStatus,
  getPayable,
  groupSplitParts,
} from "./splitPartUtils";

const STATUS_LABEL: Record<string, string> = {
  paid: "Paid",
  external: "Captured manually",
  pending: "Pending",
};

// StatusBadge's shared map has no "paid"/"external" yet, so part statuses pick their tone here.
const STATUS_TONE: Record<string, BadgeTone> = {
  paid: "success",
  external: "info",
  pending: "warning",
};

/**
 * Routed, this is a full page; inside the student profile it is one tab, so it must not
 * add a second .page-body (which carries the shell's header and sidebar offsets).
 */
const SplitRoot: React.FC<{ embedded: boolean; children: React.ReactNode }> = ({ embedded, children }) =>
  embedded ? <div className="ds-page split-page split-page--embedded">{children}</div> : <Page className="split-page">{children}</Page>;

type SplitPaymentListProps = {
  // supplied when rendered inside the student profile; otherwise read from the route
  credentialId?: string;
  studentId?: string;
  profileData?: any;
};

const SplitPaymentList: React.FC<SplitPaymentListProps> = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const credentialId = props.credentialId ?? params.credentialId;
  const studentId = props.studentId ? String(props.studentId) : params.studentId;
  const embedded = Boolean(props.studentId);

  const [groups, setGroups] = useState<SplitGroup[]>([]);
  const [profileData, setProfileData] = useState<any>(props.profileData || null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [externalPart, setExternalPart] = useState<SplitPart | null>(null);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editPart, setEditPart] = useState<SplitPart | null>(null);
  const [lookupCredentialId, setLookupCredentialId] = useState<string>("");
  const [lookupStudentId, setLookupStudentId] = useState<string>("");

  const loadPackages = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    try {
      const items = await getPackageList(studentId);
      setGroups(groupSplitParts(Array.isArray(items) ? items : []));
    } catch (e) {
      console.error("Error loading split payments", e);
      setError("Could not load split payments for this student.");
    }
    setLoading(false);
  }, [studentId]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    // CustomPayment needs the full profile to build the external payment request
    if (props.profileData) {
      setProfileData(props.profileData);
      return;
    }
    if (!credentialId || !studentId) return;
    studentProfile(credentialId, studentId)
      .then(setProfileData)
      .catch((e) => console.error("Error loading student profile", e));
  }, [credentialId, studentId, props.profileData]);

  // Split links are copied with the part id and the same utm tagging the
  // Share Link action uses elsewhere, so a forwarded link stays attributable.
  const buildShareLink = (part: SplitPart): string => {
    const profile = JSON.parse(localStorage.profileData || "{}");
    const cleanedUserName = String(profile?.firstName || "").trim().replace(/\s+/g, "_");
    const params = [
      part.split_package_id ? `sid=${encodeURIComponent(part.split_package_id)}` : "",
      "utm_source=admin",
      `utm_campaign=${encodeURIComponent(cleanedUserName)}`,
    ].filter(Boolean).join("&");
    const separator = String(part.packageLink).includes("?") ? "&" : "?";
    return `${part.packageLink}${separator}${params}`;
  };

  const copyLink = (part: SplitPart) => {
    if (!part.packageLink) {
      toast.error("No payment link on this part.");
      return;
    }
    const link = buildShareLink(part);
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("Payment link copied"))
      .catch(() => window.open(link, "_blank"));
  };

  const studentLabel = profileData
    ? `${profileData.firstName || ""} ${profileData.lastName || ""}`.trim()
    : "";

  if (!studentId) {
    // Reached from the sidebar with no student context. Both ids are on the student
    // profile URL (/pages/student-profile/:credentialId/:studentId).
    return (
      <Page className="split-page">
        <PageHeader
          eyebrow="Sales"
          title="Split Payment"
          subtitle="Enter a student to see their payment links, or open Split Payment from the student profile."
        />
        <Card>
          <Toolbar>
            <Field label="Credential ID" className="ds-toolbar__grow">
              {(ctl) => (
                <TextInput
                  {...ctl}
                  placeholder="Credential ID"
                  value={lookupCredentialId}
                  onChange={(e) => setLookupCredentialId(e.target.value)}
                />
              )}
            </Field>
            <Field label="Student ID" className="ds-toolbar__grow">
              {(ctl) => (
                <TextInput
                  {...ctl}
                  placeholder="Student ID"
                  value={lookupStudentId}
                  onChange={(e) => setLookupStudentId(e.target.value)}
                />
              )}
            </Field>
            <div className="split-lookup-action">
              <Button
                variant="primary"
                disabled={!lookupCredentialId || !lookupStudentId}
                onClick={() =>
                  navigate(
                    `${process.env.PUBLIC_URL}/pages/split-payment/${lookupCredentialId.trim()}/${lookupStudentId.trim()}`
                  )
                }
              >
                View
              </Button>
            </div>
          </Toolbar>
        </Card>
      </Page>
    );
  }

  const profileLoader = <Loader label="Loading student profile…" />;

  return (
    <SplitRoot embedded={embedded}>
      <PageHeader
        eyebrow={embedded ? undefined : "Sales"}
        title="Split Payment"
        subtitle={`${studentLabel ? `Student: ${studentLabel} · ` : ""}Student Id: ${studentId}`}
        actions={
          <Button variant="primary" icon={<Plus size={16} aria-hidden />} onClick={() => setCreateOpen(true)}>
            Create split payment
          </Button>
        }
      />

      {loading && <Loader label="Loading split payments…" />}
      {!loading && error && (
        <Callout tone="danger" title="Split payments didn't load">
          <p>{error}</p>
          <Button size="sm" variant="secondary" className="split-retry" onClick={loadPackages}>
            Try again
          </Button>
        </Callout>
      )}
      {!loading && !error && !groups.length && (
        <EmptyState
          title="No split payments for this student yet"
          description="Use Create split payment to spread a package over several payment links."
        />
      )}

      {groups.map((group) => {
        const collectedPercent = group.total ? Math.min((group.collected / group.total) * 100, 100) : 0;
        return (
          <Card
            key={group.groupId}
            flush
            title={group.courseName}
            subtitle={`Teacher: ${group.teacherName} · Created: ${formatCreatedOn(group.createdOn)}`}
            actions={
              <div className="split-collected">
                <Badge tone="primary">{group.parts.length} payment links</Badge>
                <div className="ds-text-muted">
                  <span className="ds-num ds-strong split-collected__value">
                    {formatMoney(group.collected, group.parts[0]?.currency)}
                  </span>{" "}
                  collected of <span className="ds-num">{formatMoney(group.total, group.parts[0]?.currency)}</span>
                </div>
                <progress
                  className="split-progress"
                  max={100}
                  value={collectedPercent}
                  aria-label={`${Math.round(collectedPercent)}% collected`}
                />
              </div>
            }
          >
            <ul className="split-parts">
              {group.parts.map((part, index) => {
                const status = getPartStatus(part);
                // fall back to the course currency if the row itself is missing one
                const currency = part.currency || part.courseData?.[0]?.currency;
                const expired = status === "pending" && isExpired(part);
                // Parts are collected in order, so a link cannot be shared or settled
                // until every earlier part in its group has been.
                const locked = !group.parts.slice(0, index).every(isSettled);
                const lockedReason = `Settle part ${index} of ${group.parts.length} first`;
                return (
                  <li className="split-row" key={part.split_package_id || part.createdOn}>
                    <Badge className="split-row__part">
                      Part {part.split_part} of {part.split_parts_total || group.parts.length}
                    </Badge>

                    <div className="split-row__detail">
                      <div className="ds-strong">{describeLevels(part)}</div>
                      <div className="ds-text-muted split-row__amounts">
                        {getClassCount(part)} classes · {formatMoney(getGross(part), currency)} price ·{" "}
                        {formatMoney(getDiscount(part), currency)} off ({getDiscountPercent(part).toFixed(2)}%) ·{" "}
                        <span className="ds-strong split-row__payable">
                          {formatMoney(getPayable(part), currency)} {status === "pending" ? "payable" : "paid"}
                        </span>{" "}
                        <span className="ds-text-subtle">({getCurrencyCode(currency)})</span>
                      </div>
                      {part.expiryDate && (
                        // only a still-payable link can meaningfully be expired
                        <div className={expired ? "split-row__expiry split-row__expiry--expired" : "split-row__expiry ds-text-subtle"}>
                          {expired ? "Link expired" : "Link expires"} {formatExpiry(part.expiryDate)}
                        </div>
                      )}
                      {locked && <div className="split-row__expiry ds-text-subtle">{lockedReason}</div>}
                    </div>

                    <div className="split-row__actions">
                      <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={locked}
                        title={locked ? lockedReason : "Copy the payment link"}
                        onClick={() => copyLink(part)}
                      >
                        Copy link
                      </Button>
                      {status === "pending" && (
                        <>
                          <Button size="sm" variant="secondary" onClick={() => setEditPart(part)}>
                            Edit link
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={locked}
                            title={locked ? lockedReason : "Record a payment collected outside Razorpay"}
                            onClick={() => setExternalPart(part)}
                          >
                            Capture manual payment
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}

      {/* Same form again, prefilled with the part - saves in place, keeping its link */}
      <Dialog
        open={Boolean(editPart)}
        onClose={() => setEditPart(null)}
        size="xl"
        title={
          <>
            Edit payment link — Part {editPart?.split_part} of {editPart?.split_parts_total}
          </>
        }
      >
        {profileData ? (
          <CustomPayment
            isSplit={true}
            profileData={profileData}
            studentId={studentId}
            selectedPackage={editPart}
            saveCallBack={() => {
              setEditPart(null);
              loadPackages();
            }}
            setPaymentModal={() => setEditPart(null)}
          />
        ) : (
          profileLoader
        )}
      </Dialog>

      {/* Same form as Create Package, in split mode */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} size="xl" title="Create split payment">
        {profileData ? (
          <CustomPayment
            isSplit={true}
            profileData={profileData}
            studentId={studentId}
            saveCallBack={() => {
              setCreateOpen(false);
              loadPackages();
            }}
            setPaymentModal={() => setCreateOpen(false)}
          />
        ) : (
          profileLoader
        )}
      </Dialog>

      {/* Reuses the existing external payment form, prefilled from the part */}
      <Dialog
        open={Boolean(externalPart)}
        onClose={() => setExternalPart(null)}
        size="xl"
        title={
          <>
            Capture manual payment — Part {externalPart?.split_part} of {externalPart?.split_parts_total} ·{" "}
            {formatMoney(getPayable(externalPart || {}), externalPart?.currency)}
          </>
        }
      >
        {profileData ? (
          <CustomPayment
            isExternal={true}
            profileData={profileData}
            studentId={studentId}
            selectedPackage={externalPart}
            saveCallBack={() => {
              setExternalPart(null);
              loadPackages();
            }}
            setPaymentModal={() => setExternalPart(null)}
          />
        ) : (
          profileLoader
        )}
      </Dialog>
    </SplitRoot>
  );
};

export default SplitPaymentList;
